import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment/environment';
import type { CandidateDashboardDTO } from '@libs/dtos/merit/candidate-dashboard.dto';
import type { CompanyDashboardDTO } from '@libs/dtos/merit/company-dashboard.dto';
import type { CandidatePortalMeDTO, CompanyPortalMeDTO } from '@libs/dtos/merit/portal.dto';
import { type Observable, shareReplay, tap } from 'rxjs';
import { SessionCacheRegistry } from '../shared/session-cache.registry';

/** Sanitized "me" views for the external portals (no score ever). */
@Injectable({ providedIn: 'root' })
export class PortalService {
	private readonly sessionCaches = inject(SessionCacheRegistry);

	constructor() {
		// Cerrar sesión no recarga la página, así que esta caché sobreviviría
		// al cambio de cuenta si no se vaciara aquí.
		this.sessionCaches.register(() => {
			this.invalidateCandidateMe();
		});
	}

	private readonly http = inject(HttpClient);
	private readonly base = `${environment.pathBackend}/portal`;
	private candidateMe$: Observable<CandidatePortalMeDTO> | null = null;

	/**
	 * Cacheada porque el guard del portal la pide en CADA navegación
	 * (`candidateLimitedAccessGuard` es `canActivateChild` de todo `/candidate`):
	 * una sesión de 20 clics eran 20 GET de un DTO con ~25 campos, sus bloques de
	 * validación y una URL firmada por documento.
	 *
	 * `refCount: false` a propósito: entre navegación y navegación no queda nadie
	 * suscrito, y con `refCount: true` la caché se tiraría justo entre las dos.
	 */
	getCandidateMe(): Observable<CandidatePortalMeDTO> {
		if (!this.candidateMe$) {
			this.candidateMe$ = this.http
				.get<CandidatePortalMeDTO>(`${this.base}/candidate/me`)
				.pipe(shareReplay({ bufferSize: 1, refCount: false }));
		}
		return this.candidateMe$;
	}

	/** Devuelve la ficha corregida a la cola de validacion. */
	resubmitCandidate(): Observable<{ ok: boolean }> {
		// Reenviar cambia el estado que la caché guarda: sin invalidar, la
		// pantalla seguiría diciendo "corrige tu ficha" después de corregirla.
		return this.http
			.post<{ ok: boolean }>(`${this.base}/candidate/resubmit`, {})
			.pipe(tap(() => this.invalidateCandidateMe()));
	}

	/** Tira la caché de `me`. Llamar tras cualquier cambio del propio perfil. */
	invalidateCandidateMe(): void {
		this.candidateMe$ = null;
	}

	getCompanyMe(): Observable<CompanyPortalMeDTO> {
		return this.http.get<CompanyPortalMeDTO>(`${this.base}/company/me`);
	}

	/**
	 * M12 §5 · `e-dashboard`. Every figure of the company home in one read:
	 * KPIs, 72h window, unreviewed applicants, vacancies and plan usage.
	 */
	getCompanyDashboard(): Observable<CompanyDashboardDTO> {
		return this.http.get<CompanyDashboardDTO>(`${this.base}/company/dashboard`);
	}

	/**
	 * "Exportar resumen" — the same home, laid out as a PDF by the server.
	 *
	 * Built there and not here on purpose: the export used to be a CSV assembled
	 * in the browser, which could only carry the KPI column. The document the
	 * client asked for is the dashboard itself, panels included, and that is a
	 * layout — not a table.
	 */
	getCompanyDashboardPdf(): Observable<Blob> {
		return this.http.get(`${this.base}/company/dashboard/pdf`, { responseType: 'blob' });
	}

	/**
	 * M12 §5 · `c-dashboard`. The candidate home in one read: live applications,
	 * recommended vacancies, profile completion and unread counters. Never a
	 * K-Score — the candidate does not get to see theirs (M02 §4, M03).
	 */
	getCandidateDashboard(): Observable<CandidateDashboardDTO> {
		return this.http.get<CandidateDashboardDTO>(`${this.base}/candidate/dashboard`);
	}
}
